'use client';

import { MaterialReactTable } from 'material-react-table';
import { Download } from '@mui/icons-material';
import { Box, Button, IconButton, ListItemIcon, Menu, MenuItem, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import { useQueryGoaEvents, useUpdateGoaEvent } from '@/hooks/api/useGoas';
import { useNotification } from '@/contexts/NotificationContext';
import { formatDate } from '@/stack-shared/utils/gen-utils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const GoaEventPage: React.FC = () => {
  const [data, setData] = useState([] as any[]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [sorting, setSorting] = useState([] as any[]);
  const [search, setSearch] = useState('');
  const [assetIdFilter, setAssetIdFilter] = useState('');
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const now = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);

  const [startDate, setStartDate] = useState<Date | null>(lastMonth);
  const [endDate, setEndDate] = useState<Date | null>(now);

  const queryGoaEventsMutation = useQueryGoaEvents();
  const updateGoaEventMutation = useUpdateGoaEvent();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();

  const columns = [
    { accessorKey: 'id', header: 'ID' },
    {
      accessorKey: 'date', header: 'Date',
      Cell: ({ row }: any) => {
        return formatDate(row?.original?.date);
      },
    },
    { accessorKey: 'time', header: 'Time' },
    { accessorKey: 'locationName', header: 'Location' },
    { accessorKey: 'assetId', header: 'Asset ID' },
    { accessorKey: 'statusChange', header: 'Status Change' },
    { accessorKey: 'event', header: 'Event' },
    { accessorKey: 'technician', header: 'Technician' },
    {
      accessorKey: 'resolveDate', header: 'Resolve Date',
      Cell: ({ row }: any) => {
        return formatDate(row?.original?.resolveDate);
      },
    },
    { accessorKey: 'resolveTime', header: 'Resolve Time' },
    { accessorKey: 'maxBet', header: 'Max Bet' },
    { accessorKey: 'payoutLevel', header: 'Payout Level' },
    { accessorKey: 'moneyIn', header: 'Money In' },
    { accessorKey: 'moneyOut', header: 'Money Out' },

  ];

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'GOA Events' },
  ];

  const rightContent = (
    <>
      <Button variant="outlined" startIcon={<Download />} sx={{ textTransform: 'none' }}>
        Export
      </Button>
      {/* <Button onClick={() => setCreateModalVisible(true)} variant="contained" sx={{ textTransform: 'none' }}>
        + Add Event
      </Button> */}
    </>
  );

  const sharedEventFields = [{
    groupName: 'main',
    fields: [
      { label: 'Date', name: 'date', type: GENERIC_FORM_FIELD_TYPE.DATE, required: true, columns: 6 },
      { label: 'Time', name: 'time', type: GENERIC_FORM_FIELD_TYPE.TIME, required: false, columns: 6 },
      { label: 'Hard In', name: 'hardIn', type: GENERIC_FORM_FIELD_TYPE.FLOAT, required: false, columns: 6 },
      { label: 'Hard Out', name: 'hardOut', type: GENERIC_FORM_FIELD_TYPE.FLOAT, required: false, columns: 6 },
      { label: 'Asset ID', name: 'assetId', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6, disabled: true },
      { label: 'Event', name: 'event', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 6, disabled: true },
    ],
  }];

  const editEventFields = cloneDeep(sharedEventFields);

  const createEventFields = cloneDeep(sharedEventFields);
  createEventFields[0].fields.push({ label: 'Status Change', name: 'statusChange', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 });



  const fetchData = async () => {
    if (!startDate || !endDate) return;

    queryGoaEventsMutation.mutate(
      {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        skip: pagination.pageIndex * pagination.pageSize,
        take: pagination.pageSize,
        search,
        orderBy: sorting[0]?.id || 'id',
        orderDirection: sorting[0]?.desc ? 'DESC' : 'ASC',
        assetId: assetIdFilter || null,
      },
      {
        onSuccess: (result: any) => {
          setData(result.data || []);
          setTotalRows(result.total || 0);
        },
        onError: (error: any) => {
          showError(error.message || 'Failed to fetch GOA events');
        },
      }
    );
  };

  useEffect(() => {
    fetchData();
  }, [pagination, sorting, search, tableRefreshKey, startDate, endDate, assetIdFilter]);

  return (
    <>
      <GenericFormModal
        visible={createModalVisible}
        onDismiss={() => setCreateModalVisible(false)}
        onSubmit={async (values: any) => {
          console.log("values?", values);
          // Dispatch create action
          setCreateModalVisible(false);
          setTableRefreshKey(tableRefreshKey + 1);
        }}
        initialValues={{}}
        fieldGroups={createEventFields}
        title={'Create GOA Event'}
      />

      <GenericFormModal
        visible={editModalVisible}
        onDismiss={() => setEditModalVisible(false)}
        onSubmit={async (values: any) => {
          updateGoaEventMutation.mutate(values, {
            onSuccess: (data: any) => {
              console.log("Updated", data);
              setEditModalVisible(false);
              setTableRefreshKey(tableRefreshKey + 1);
              showSuccess("GOA event updated successfully");
            },
            onError: (error: any) => {
              showError(error.message || 'Failed to update GOA event');
            },
          });
        }}
        initialValues={{
          id: selectedEvent?.id,
          date: selectedEvent?.date,
          time: selectedEvent?.time,
          hardIn: selectedEvent?.hardIn,
          hardOut: selectedEvent?.hardOut,
          assetId: selectedEvent?.assetId,
          event: selectedEvent?.event,
        }}
        fieldGroups={editEventFields}
        title={'Edit GOA Event'}
      />

      <TitleBar breadcrumbs={breadcrumbs} title="GOA Events" rightContent={rightContent} />

      <MaterialReactTable
        columns={[
          { accessorKey: 'id', header: 'ID' },
          { accessorKey: 'assetId', header: 'Asset ID' },
          { 
            accessorKey: 'date', 
            header: 'Date',
            Cell: ({ row }) => {
              const date = new Date(row.original.date);
              return date.toLocaleString('en-CA', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }).replace(',', '');
            }
          },
          { accessorKey: 'event', header: 'Event' },
          { accessorKey: 'locationName', header: 'Location' },
          { 
            accessorKey: 'hardIn', 
            header: 'Hard In',
            Cell: ({ row }) => Number(row.original.hardIn || 0).toFixed(2)
          },
          { 
            accessorKey: 'hardOut', 
            header: 'Hard Out',
            Cell: ({ row }) => Number(row.original.hardOut || 0).toFixed(2)
          },
          {
            id: 'actions',
            header: 'Actions',
            Cell: ({ row }: any) => {
              const rowData = row.original;
              const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
              const actionsOpen = Boolean(actionsAnchorEl);
      
              const handleMenuOpen = (event: any) => {
                setActionsAnchorEl(event.currentTarget);
              };
      
              const handleMenuClose = () => {
                setActionsAnchorEl(null);
              };
      
              return (
                <>
                  <IconButton onClick={handleMenuOpen} color="primary" title="Options">
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={actionsAnchorEl}
                    open={actionsOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <MenuItem onClick={() => {
                      console.log("selected event?", rowData)
                      setSelectedEvent(rowData);
                      setEditModalVisible(true);
                    }}>
                      <ListItemIcon><EditIcon /></ListItemIcon>
                      <Typography>Edit</Typography>
                    </MenuItem>
                  </Menu>
                </>
              );
            },
          },
        ]}
        data={data}
        manualPagination
        manualSorting
        enableGlobalFilter={false}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        rowCount={totalRows}
        state={{ isLoading, pagination, sorting }}
        initialState={{ density: 'compact' }}
        renderTopToolbarCustomActions={() => (
          <Box display="flex" gap={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box display="flex" gap={2}>
                {/* Start Date Picker */}
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => {
                    if (newValue && typeof newValue === 'object' && 'toDate' in newValue) {
                      setStartDate(newValue.toDate());
                    } else if (newValue instanceof Date) {
                      setStartDate(newValue);
                    } else {
                      setStartDate(null);
                    }
                  }}
                  slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
                />

                {/* End Date Picker */}
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => {
                    if (newValue && typeof newValue === 'object' && 'toDate' in newValue) {
                      setEndDate(newValue.toDate());
                    } else if (newValue instanceof Date) {
                      setEndDate(newValue);
                    } else {
                      setEndDate(null);
                    }
                  }}
                  slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
                />
              </Box>
            </LocalizationProvider>

            <TextField
              label="Asset ID"
              variant="outlined"
              size="small"
              value={assetIdFilter}
              onChange={(e) => setAssetIdFilter(e.target.value)}
              sx={{ width: '120px' }}
            />

            <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: '250px' }}
            />
          </Box>
        )}
      />
    </>
  );
};

export default GoaEventPage;
